import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';

import { NewSourcePreviewButtonComponent } from '../../components/partials/source-preview/new-button/new-source-preview-button.component';
import { TAdaptedReferenceV3 } from '../../types/types';

@Directive({ selector: '[appReferenceInject]' })
export class ReferenceInjectDirective implements OnChanges, OnDestroy {
  @Input('appReferenceInject') ref!: TAdaptedReferenceV3[] | undefined;
  @Output() referenceClicked = new EventEmitter<any>();

  private componentRefs: ComponentRef<NewSourcePreviewButtonComponent>[] = [];
  private retryTimeout: any;
  private observer: MutationObserver | null = null;

  constructor(
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private resolver: ComponentFactoryResolver,
    private injector: Injector
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ref) {
      this.clearComponents();
      this.cleanupObserver();
      if (this.ref?.length) {
        this.createComponentsWithRetry();
      }
    }
  }

  private createComponentsWithRetry(): void {
    const hostElement = this.viewContainerRef.element.nativeElement;

    // Try to create components immediately
    if (this.tryCreateComponents(hostElement)) {
      return;
    }

    // If innerHTML hasn't rendered yet (async markdown pipe), watch for content changes
    this.observer = new MutationObserver(() => {
      if (this.tryCreateComponents(hostElement)) {
        this.cleanupObserver();
      }
    });

    this.observer.observe(hostElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Fallback timeout in case MutationObserver doesn't catch it
    this.retryTimeout = setTimeout(() => {
      this.tryCreateComponents(hostElement);
      this.cleanupObserver();
    }, 100);
  }

  private tryCreateComponents(hostElement: HTMLElement): boolean {
    const mermaidDiv = hostElement.querySelector('div.mermaid');

    // For mermaid diagrams, append to the host element directly.
    // For regular content, always append reference buttons at the END of the host element
    // (not deep inside nested markdown elements like <li>, <td>, etc.)
    // This ensures references appear after all the paragraph's rendered content.
    const targetElement = mermaidDiv
      ? hostElement
      : this.findAppendTarget(hostElement);

    if (!targetElement) return false;

    // Clear any existing components before creating new ones
    this.clearComponents();

    this.ref?.forEach(reference => {
      const labels = reference.uiDisplayLabels?.length
        ? reference.uiDisplayLabels
        : [reference.uiDisplayLabel ?? undefined];

      labels.forEach((label, labelIndex) => {
        const factory = this.resolver.resolveComponentFactory(NewSourcePreviewButtonComponent);
        const componentRef = factory.create(this.injector);

        componentRef.instance.adaptedReference = reference;
        componentRef.instance.displayLabelOverride = label ?? null;
        componentRef.instance.externalReferenceIndex =
          reference.uiDisplayLabels?.length ? labelIndex : null;
        componentRef.instance.buttonClicked.subscribe((event: any) => {
          this.referenceClicked.emit(event);
        });

        targetElement.appendChild(componentRef.location.nativeElement);

        componentRef.hostView.detectChanges();

        this.componentRefs.push(componentRef);
      });
    });

    return true;
  }

  /**
   * Find the best element to append reference buttons to.
   * We want references INLINE at the end of the last visible text,
   * so they appear to the right of the final words -- not on a new line below.
   *
   * For lists: drill into the last <li>
   * For tables: drill into the last <td>
   * For paragraphs/inline: append directly
   */
  private findAppendTarget(hostElement: HTMLElement): HTMLElement | null {
    if (!hostElement.textContent?.trim()) return null;

    const children = Array.from(hostElement.children) as HTMLElement[];
    if (children.length === 0) return hostElement;

    // Find the last child with actual text content
    const lastWithText = [...children].reverse().find(child => child.textContent?.trim().length);
    if (!lastWithText) return hostElement;

    const tag = lastWithText.tagName?.toLowerCase();

    // Inline elements and paragraphs -- append directly
    if (['p', 'span', 'em', 'strong', 'a', 'code'].includes(tag)) {
      return lastWithText;
    }

    // Lists -- find the last <li> and append inside it (inline with the text)
    if (tag === 'ul' || tag === 'ol') {
      const lastLi = this.findLastElementWithText(lastWithText, 'li');
      return lastLi || lastWithText;
    }

    // Tables -- find the last <td>/<th> with text
    if (tag === 'table') {
      const lastCell = this.findLastElementWithText(lastWithText, 'td')
        || this.findLastElementWithText(lastWithText, 'th');
      return lastCell || hostElement;
    }

    // Blockquote -- find the last paragraph or inline child inside it
    if (tag === 'blockquote') {
      const lastP = this.findLastElementWithText(lastWithText, 'p');
      return lastP || lastWithText;
    }

    return hostElement;
  }

  /**
   * Find the last descendant element matching a tag name that has text content.
   */
  private findLastElementWithText(container: HTMLElement, tagName: string): HTMLElement | null {
    const elements = Array.from(container.querySelectorAll(tagName)) as HTMLElement[];
    return [...elements].reverse().find(el => el.textContent?.trim().length) || null;
  }

  private cleanupObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  private clearComponents(): void {
    this.componentRefs.forEach(componentRef => {
      this.renderer.removeChild(
        componentRef.location.nativeElement.parentNode,
        componentRef.location.nativeElement
      );
      componentRef.destroy();
    });
    this.componentRefs = [];
  }

  ngOnDestroy(): void {
    this.clearComponents();
    this.cleanupObserver();
  }
}
